import { useState } from "react";
import FeatureModal from "./FeatureModal";
import { Factory, Box, Leaf, RefreshCw, Globe, ShieldCheck } from "lucide-react";

// ── Replace with your actual video file ──────────────────────────────────────
import capabilitiesVideo from "../assets/dashboard-provinyx.mp4";
// ─────────────────────────────────────────────────────────────────────────────

const featuresData = [
  {
    icon: <Factory size={24} />,
    title: "Manufacturing & Supply Chain",
    subtitle: "Structured material composition to support repair, recycling services, and enable informed consumer decisions throughout the product lifecycle.",
    overview: "Gain end-to-end visibility from raw materials to finished goods. This platform tracks your complete product journey, guaranteeing supply chain transparency and verifiable compliance with global regulations.",
    features: ["Real-time supply chain tracking for materials and components", "Automated supplier verification and certification management", "Streamlined compliance documentation for global regulations", "Immutable, blockchain-based provenance for data integrity"],
    benefits: ["Enhanced supply chain visibility", "Reduced compliance risks", "Improved supplier relationships", "Faster time to market"],
    useCase: "A luxury fashion brand uses Provinyx to track organic cotton from certified farms through spinning, weaving, and garment manufacturing, giving consumers full transparency.",
  },
  {
    icon: <Box size={24} />,
    title: "Materials & Composition",
    subtitle: "Structured material composition to support repair, recycling services, and enable informed consumer decisions throughout the product lifecycle.",
    overview: "Build and maintain thorough material inventories that enable repair, refurbishment, and recycling at scale. Full chemical and component documentation ensures regulatory compliance while supporting sustainable, circular economy practices.",
    features: ["Detailed Bill of Materials with full material composition breakdowns", "Transparent chemical substance disclosure and hazardous material tracking", "Safety data sheets and material certification repository", "Recyclability scoring paired with actionable end-of-life instructions"],
    benefits: ["Consumer trust building", "Simplified recycling", "Repair service enablement", "Regulatory compliance"],
    useCase: "An electronics manufacturer provides full component-level material data, making responsible recycling straightforward for consumers and recyclers alike.",
  },
  {
    icon: <Leaf size={24} />,
    title: "Sustainability Certifications",
    subtitle: "Follow EU rules easily with checked environmental data, trusted green certificates, and clear carbon tracking.",
    overview: "Store all your green certificates, eco-impact scores, and carbon data securely in one place. It automatically checks that your certificates are valid and keeps you following the latest EU laws.",
    features: ["Track your product's carbon footprint from beginning to end", "Monitor how much water is used and waste is produced", "Manage information about your product's total environmental life", "Make sure you meet EU rules for green design and energy labels."],
    benefits: ["Verified sustainability claims", "Reduced greenwashing risk", "Enhanced brand credibility", "Regulatory readiness"],
    useCase: "A sportswear brand links all sustainability certifications to individual products, building trust with eco-conscious consumers.",
  },
  {
    icon: <RefreshCw size={24} />,
    title: "Repair & Circularity Services",
    subtitle: "Make it simple for customers to repair, recycle, and resell your products, cutting down on waste.",
    overview: "Turn a single-use product into something that lasts. Help buyers find repair services, recycling centers, and second-hand markets to give your products a longer life.",
    features: ["Direct links to approved repair centers", "Online catalogs for ordering spare parts", "Easy management for customer take-back and recycling programs", "Connections to second-hand shops and rental services", "Tracking for product warranties and service history"],
    benefits: ["New revenue streams", "Customer lifecycle engagement", "Waste reduction", "Brand loyalty"],
    useCase: "A luxury watch brand uses Provinyx to connect customers with certified repair partners, extending product lifetimes and maintaining value.",
  },
  {
    icon: <Globe size={24} />,
    title: "Interoperable & Open Standards",
    subtitle: "Uses universally accepted standards to create simple, searchable product information that machines can easily understand.",
    overview: "Make sure your digital product passports connect easily with other systems and work perfectly across borders. By sticking to open rules, your technology will always be ready for the future.",
    features: ["Meets rules like GS1 Digital Link and EPCIS", "Connects your tools smoothly with RESTful API", "Supports multiple languages and types of money", "Rules for sharing information between different types of businesses"],
    benefits: ["Vendor independence", "Future-proof architecture", "Ecosystem compatibility", "Reduced integration costs"],
    useCase: "A retail consortium standardizes product data across 50+ brands using Provinyx's open API, enabling unified consumer experience.",
  },
  {
    icon: <ShieldCheck size={24} />,
    title: "Secure Authentication",
    subtitle: "A safe way to check if products are real, stop fakes, and keep their value high.",
    overview: "Protect your brand and your buyers with enterprise-level safety. We use blockchain, secret digital codes, and physical tags to guarantee your products are genuine and stop counterfeiters.",
    features: ["Unchangeable blockchain records for every item", "Both physical and digital checks to prove authenticity", "QR codes that get damaged  if tampered with", "Fast reporting when fake products are spotted", "Secure tracking of the product's whole journey"],
    benefits: ["Brand protection", "Consumer confidence", "Grey market prevention", "Liability reduction"],
    useCase: "A pharmaceutical company uses Provinyx to verify drug authenticity at every step from manufacturer to patient, preventing dangerous counterfeits.",
  },
];

function Capabilities() {
  const [selected, setSelected] = useState(null);

  return (
    <>
      <style>{`
        .video-wrapper {
          position: relative;
          border-radius: 20px !important;
          overflow: hidden;
          -webkit-mask-image: -webkit-radial-gradient(white, black);
          box-shadow:
            0 2px 4px rgba(0,0,0,0.04),
            0 8px 24px rgba(0,0,0,0.09),
            0 24px 56px rgba(37,99,235,0.10);
         // transition: transform 0.4s cubic-bezier(0.34,1.2,0.64,1),
          //  box-shadow 0.4s ease;
        }
        .video-wrapper:hover {
          //transform: translateY(-4px) scale(1.005);
          //box-shadow:
           // 0 4px 8px rgba(0,0,0,0.06),
           // 0 16px 40px rgba(0,0,0,0.12),
           // 0 40px 80px rgba(37,99,235,0.14);
        }
        .video-wrapper::after {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 20px;
          box-shadow: inset 0 0 0 1px rgba(255,255,255,0.16);
          pointer-events: none;
          z-index: 4;
        }
        .video-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            160deg,
            rgba(8,12,40,0.18) 0%,
            rgba(8,12,40,0.02) 50%,
            rgba(8,12,40,0.24) 100%
          );
          pointer-events: none;
          z-index: 2;
        }
        .video-bar {
          position: absolute;
          bottom: 0; left: 0; right: 0;
          height: 3px;
          background: rgba(255,255,255,0.18);
          z-index: 3;
        }
      `}</style>

      <section id="features" className="py-16 sm:py-20 lg:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">

          {/* Badge */}
          <div className="inline-block bg-blue-100 text-blue-600 px-4 py-2 rounded-full text-sm font-medium">
            Platform Capabilities
          </div>

          {/* Heading */}
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mt-5">
            WHAT IS PROVINYX?
          </h2>

          {/* Subtext */}
          <p className="text-gray-500 mt-5 max-w-2xl mx-auto text-base sm:text-lg leading-relaxed">
            Provinyx functions as a digital product passport, assigning unique identities to physical items and enabling instant access to comprehensive lifecycle information via scannable QR codes.
          </p>

          <div className="mt-12 sm:mt-14 max-w-6xl mx-auto">
            <div className="video-wrapper">
              <video
                src={capabilitiesVideo}
                className="w-full block"
                style={{ display: "block", objectFit: "contain", borderRadius: "20px" }}
                autoPlay
                muted
                loop
                playsInline
              />
              <div className="video-overlay" />
              <div className="video-bar" />
            </div>
          </div>

          {/* Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 mt-16 sm:mt-20">
            {featuresData.map((item, index) => (
              <div
                key={index}
                onClick={() => setSelected(item)}
                className="
                  group bg-white text-left cursor-pointer
                  px-7 py-8
                  rounded-2xl
                  border border-gray-200
                  transition-all duration-300 ease-out
                  hover:border-blue-500
                  hover:-translate-y-2
                  hover:shadow-[0_20px_50px_rgba(0,0,0,0.12)]
                "
              >
                {/* Icon box */}
                <div className="
                  w-11 h-11 flex items-center justify-center rounded-xl mb-5
                  bg-blue-100 text-blue-600
                  transition-all duration-300
                  group-hover:bg-blue-600 group-hover:text-white
                ">
                  {item.icon}
                </div>

                {/* Title */}
                <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors duration-200 leading-snug">
                  {item.title}
                </h3>

                {/* Divider */}
                <div className="h-px bg-gray-100 group-hover:bg-blue-100 my-4 transition-colors duration-300" />

                {/* Subtitle */}
                <p className="text-gray-500 text-sm leading-relaxed">
                  {item.subtitle}
                </p>

                {/* CTA */}
                <p className="
                  mt-5 text-sm font-semibold text-blue-600
                  flex items-center gap-1
                  opacity-0 translate-y-2
                  group-hover:opacity-100 group-hover:translate-y-0
                  transition-all duration-300
                ">
                  Learn more <span className="text-base">→</span>
                </p>
              </div>
            ))}
          </div>

        </div>
      </section>

      {selected && <FeatureModal data={selected} onClose={() => setSelected(null)} />}
    </>
  );
}

export default Capabilities;